import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Plus, Check, User, ChartBar, CalendarCheck, Car } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const Transportation = ({ user }) => {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [bookingsRes, driversRes, dashboardRes] = await Promise.all([
        api.get('/transportation/bookings'),
        api.get('/transportation/drivers'),
        api.get('/transportation/dashboard'),
      ]);
      setBookings(bookingsRes.data);
      setDrivers(driversRes.data);
      setDashboardStats(dashboardRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/transportation/bookings', {
        guest_name: formData.get('guest_name'),
        guest_email: formData.get('guest_email'),
        guest_phone: formData.get('guest_phone'),
        pickup_location: formData.get('pickup_location'),
        destination: formData.get('destination'),
        pickup_time: formData.get('pickup_time'),
        number_of_passengers: parseInt(formData.get('number_of_passengers')),
      });
      toast.success('Booking created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create booking');
    }
  };

  const handleApproveBooking = async (bookingId) => {
    try {
      await api.put(`/transportation/bookings/${bookingId}/approve`, {
        approved_by: user.id,
      });
      toast.success('Booking approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve booking');
    }
  };

  const handleAssignDriver = async (bookingId, driverId) => {
    try {
      await api.put(`/transportation/bookings/${bookingId}/assign-driver`, {
        driver_id: driverId,
      });
      toast.success('Driver assigned');
      setSelectedBooking(null);
      loadData();
    } catch (error) {
      toast.error('Failed to assign driver');
    }
  };

  const handleCreateDriver = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/transportation/drivers', {
        name: formData.get('name'),
        phone: formData.get('phone'),
        vehicle_type: formData.get('vehicle_type'),
        vehicle_number: formData.get('vehicle_number'),
        license_number: formData.get('license_number'),
      });
      toast.success('Driver created');
      setOpenDialog(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create driver');
    }
  };

  const handleUpdateDriverStatus = async (driverId, status) => {
    try {
      await api.put(`/transportation/drivers/${driverId}/status`, { status });
      toast.success('Driver status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update driver status');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div data-testid="transportation-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2B3210] mb-2">
          Transportation
        </h1>
        <p className="text-base text-[#505631]">Manage guest transportation and driver scheduling</p>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-6 bg-[#FBF8EF] border border-[#E5E2D9]">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white" data-testid="tab-dashboard">
            <ChartBar size={16} className="mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="bookings" className="data-[state=active]:bg-white" data-testid="tab-bookings">
            <CalendarCheck size={16} className="mr-2" />
            Bookings
          </TabsTrigger>
          <TabsTrigger value="drivers" className="data-[state=active]:bg-white" data-testid="tab-drivers">
            <Car size={16} className="mr-2" />
            Drivers
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Total Bookings</p>
              <p className="text-3xl font-bold text-[#2B3210]">{dashboardStats?.total_bookings || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Pending</p>
              <p className="text-3xl font-bold text-amber-600">{dashboardStats?.pending_bookings || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Approved</p>
              <p className="text-3xl font-bold text-green-600">{dashboardStats?.approved_bookings || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Completed</p>
              <p className="text-3xl font-bold text-blue-600">{dashboardStats?.completed_bookings || 0}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Today's Bookings</p>
              <p className="text-2xl font-bold text-[#B2560D]">{dashboardStats?.today_bookings || 0}</p>
              <p className="text-xs text-[#505631] mt-1">Scheduled for today</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Upcoming (7 Days)</p>
              <p className="text-2xl font-bold text-purple-600">{dashboardStats?.upcoming_bookings || 0}</p>
              <p className="text-xs text-[#505631] mt-1">Next 7 days</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2D9] p-6">
              <p className="text-sm text-[#505631] mb-1">Drivers</p>
              <p className="text-2xl font-bold text-[#2B3210]">
                {dashboardStats?.available_drivers || 0} / {dashboardStats?.total_drivers || 0}
              </p>
              <p className="text-xs text-[#505631] mt-1">Available / Total</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Transportation Bookings</h2>
              <Dialog open={openDialog === 'booking'} onOpenChange={(open) => setOpenDialog(open ? 'booking' : null)}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-booking-button">
                    <Plus size={18} />
                    New Booking
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Transportation Booking</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateBooking} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Guest Name</label>
                      <input
                        type="text"
                        name="guest_name"
                        data-testid="booking-guest-name-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Guest Email</label>
                      <input
                        type="email"
                        name="guest_email"
                        data-testid="booking-guest-email-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Guest Phone</label>
                      <input
                        type="tel"
                        name="guest_phone"
                        data-testid="booking-guest-phone-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Pickup Location</label>
                      <input
                        type="text"
                        name="pickup_location"
                        data-testid="booking-pickup-input"
                        placeholder="e.g., Train Station"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Destination</label>
                      <input
                        type="text"
                        name="destination"
                        data-testid="booking-destination-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Pickup Time</label>
                      <input
                        type="datetime-local"
                        name="pickup_time"
                        data-testid="booking-time-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Number of Passengers</label>
                      <input
                        type="number"
                        name="number_of_passengers"
                        data-testid="booking-passengers-input"
                        min="1"
                        defaultValue="1"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <button type="submit" className="btn-primary w-full" data-testid="submit-booking-button">
                      Create Booking
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="p-6 space-y-4">
              {bookings.map((booking) => {
                const driver = drivers.find((d) => d.id === booking.driver_id);
                return (
                  <div
                    key={booking.id}
                    data-testid={`booking-card-${booking.id}`}
                    className="border border-[#E5E2D9] rounded-xl p-6"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-[#2B3210]">{booking.guest_name}</h3>
                        <p className="text-sm text-[#505631]">{booking.guest_email}</p>
                        <p className="text-sm text-[#505631]">{booking.guest_phone}</p>
                      </div>
                      <span className={`status-badge status-${booking.status}`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-[#505631] mb-1">Pickup Location</p>
                        <p className="text-sm text-[#2B3210] font-medium">{booking.pickup_location}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#505631] mb-1">Destination</p>
                        <p className="text-sm text-[#2B3210] font-medium">{booking.destination}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#505631] mb-1">Pickup Time</p>
                        <p className="text-sm text-[#2B3210] font-medium">
                          {new Date(booking.pickup_time).toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#505631] mb-1">Passengers</p>
                        <p className="text-sm text-[#2B3210] font-medium">{booking.number_of_passengers}</p>
                      </div>
                    </div>
                    {driver && (
                      <div className="p-3 bg-[#FBF8EF] rounded-lg mb-4">
                        <p className="text-xs text-[#505631] mb-1">Assigned Driver</p>
                        <p className="text-sm text-[#2B3210] font-medium">
                          {driver.name} • {driver.vehicle_type} ({driver.vehicle_number})
                        </p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      {booking.status === 'pending' && (
                        <button
                          onClick={() => handleApproveBooking(booking.id)}
                          data-testid={`approve-booking-${booking.id}`}
                          className="btn-primary text-sm"
                        >
                          Approve
                        </button>
                      )}
                      {booking.status === 'approved' && !booking.driver_id && (
                        <Dialog
                          open={selectedBooking === booking.id}
                          onOpenChange={(open) => setSelectedBooking(open ? booking.id : null)}
                        >
                          <DialogTrigger asChild>
                            <button
                              data-testid={`assign-driver-${booking.id}`}
                              className="btn-secondary text-sm flex items-center gap-2"
                            >
                              <User size={16} />
                              Assign Driver
                            </button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Assign Driver</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-2">
                              {drivers
                                .filter((d) => d.status === 'available')
                                .map((driver) => (
                                  <button
                                    key={driver.id}
                                    onClick={() => handleAssignDriver(booking.id, driver.id)}
                                    data-testid={`select-driver-${driver.id}`}
                                    className="w-full p-4 text-left border border-[#E5E2D9] rounded-lg hover:bg-[#FBF8EF] transition-colors"
                                  >
                                    <p className="font-medium text-[#2B3210]">{driver.name}</p>
                                    <p className="text-sm text-[#505631]">
                                      {driver.vehicle_type} • {driver.vehicle_number}
                                    </p>
                                  </button>
                                ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="drivers">
          <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
            <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
              <h2 className="text-xl font-medium text-[#2B3210]">Drivers</h2>
              <Dialog open={openDialog === 'driver'} onOpenChange={(open) => setOpenDialog(open ? 'driver' : null)}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-driver-button">
                    <Plus size={18} />
                    New Driver
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Driver</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateDriver} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Name</label>
                      <input
                        type="text"
                        name="name"
                        data-testid="driver-name-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        data-testid="driver-phone-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Vehicle Type</label>
                      <input
                        type="text"
                        name="vehicle_type"
                        data-testid="driver-vehicle-type-input"
                        placeholder="e.g., Sedan, SUV, Van"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">Vehicle Number</label>
                      <input
                        type="text"
                        name="vehicle_number"
                        data-testid="driver-vehicle-number-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2B3210] mb-1.5">License Number</label>
                      <input
                        type="text"
                        name="license_number"
                        data-testid="driver-license-input"
                        className="w-full px-4 py-2.5 border border-[#E5E2D9] rounded-lg"
                        required
                      />
                    </div>
                    <button type="submit" className="btn-primary w-full" data-testid="submit-driver-button">
                      Add Driver
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FBF8EF] border-b border-[#E5E2D9]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#505631] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-200">
                  {drivers.map((driver) => (
                    <tr key={driver.id} data-testid={`driver-row-${driver.id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-[#2B3210]">{driver.name}</td>
                      <td className="px-6 py-4 text-sm text-[#2B3210]">{driver.phone}</td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-[#2B3210]">{driver.vehicle_type}</div>
                        <div className="text-sm text-[#505631]">{driver.vehicle_number}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`status-badge ${
                            driver.status === 'available' ? 'status-approved' : 'status-pending'
                          }`}
                        >
                          {driver.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={driver.status}
                          onChange={(e) => handleUpdateDriverStatus(driver.id, e.target.value)}
                          data-testid={`driver-status-select-${driver.id}`}
                          className="px-3 py-1.5 border border-[#E5E2D9] rounded-lg text-sm"
                        >
                          <option value="available">Available</option>
                          <option value="busy">Busy</option>
                          <option value="off_duty">Off Duty</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Transportation;
